import _ from 'lodash-es';
import { transformAutoUpdateViewModel } from '@/react/portainer/gitops/AutoUpdateFieldset/utils';
import { StackViewModel } from '@/react/docker/stacks/view-models/stack';

angular.module('portainer.app').factory('StackService', [
  '$q',
  '$async',
  'Stack',
  'StackByName',
  'FileUploadService',
  'StackHelper',
  'ServiceService',
  'ContainerService',
  'SwarmService',
  function StackServiceFactory($q, $async, Stack, StackByName, FileUploadService, StackHelper, ServiceService, ContainerService, SwarmService) {
    'use strict';
    var service = {
      updateGit,
      updateKubeGit,
    };

    service.stack = function (id) {
      var deferred = $q.defer();

      Stack.get({ id: id })
        .$promise.then(function success(data) {
          var stack = new StackViewModel(data);
          deferred.resolve(stack);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve stack details', err: err });
        });

      return deferred.promise;
    };

    service.getStackFile = function (id, version, commitHash) {
      var deferred = $q.defer();

      Stack.getStackFile({ id: id, version: version, commitHash: commitHash })
        .$promise.then(function success(data) {
          deferred.resolve(data.StackFileContent);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve stack content', err: err });
        });

      return deferred.promise;
    };

    service.stacks = function (compose, swarm, endpointId, includeOrphanedStacks = false) {
      var deferred = $q.defer();

      var queries = [];
      if (compose) {
        queries.push(service.composeStacks(endpointId, true, { EndpointID: endpointId, IncludeOrphanedStacks: includeOrphanedStacks }));
      }
      if (swarm) {
        queries.push(service.swarmStacks(endpointId, true, { EndpointID: endpointId, IncludeOrphanedStacks: includeOrphanedStacks }));
      }

      $q.all(queries)
        .then(function success(data) {
          var stacks = [];
          if (data[0]) {
            stacks = stacks.concat(data[0]);
          }
          if (data[1]) {
            stacks = stacks.concat(data[1]);
          }
          stacks = _.uniqWith(stacks, (s1, s2) => s1.Id === s2.Id && s1.Name === s2.Name);
          deferred.resolve(stacks);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve stacks', err: err });
        });

      return deferred.promise;
    };

    service.externalSwarmStacks = function () {
      var deferred = $q.defer();

      ServiceService.services()
        .then(function success(services) {
          deferred.resolve(StackHelper.getExternalStacksFromServices(services));
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve external stacks', err: err });
        });

      return deferred.promise;
    };

    service.externalComposeStacks = function (environmentId) {
      var deferred = $q.defer();

      ContainerService.containers(environmentId, 1)
        .then(function success(containers) {
          deferred.resolve(StackHelper.getExternalStacksFromContainers(containers));
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve external stacks', err: err });
        });

      return deferred.promise;
    };

    service.unionStacks = function (stacks, externalStacks) {
      stacks.forEach((stack) => {
        externalStacks.forEach((externalStack) => {
          if (stack.Orphaned && stack.Name == externalStack.Name) {
            stack.OrphanedRunning = true;
          }
        });
      });
      const result = _.unionWith(stacks, externalStacks, function (a, b) {
        return a.Name === b.Name;
      });

      return result;
    };

    service.composeStacks = function (endpointId, includeExternalStacks, filters) {
      var deferred = $q.defer();

      $q.all({
        stacks: Stack.query({ filters: filters }).$promise,
        externalStacks: includeExternalStacks ? service.externalComposeStacks(endpointId) : [],
      })
        .then(function success(data) {
          var stacks = data.stacks.map(function (item) {
            return new StackViewModel(item, item.EndpointId != endpointId);
          });
          var externalStacks = data.externalStacks;
          const result = service.unionStacks(stacks, externalStacks);
          deferred.resolve(result);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve stacks', err: err });
        });

      return deferred.promise;
    };

    service.swarmStacks = function (endpointId, includeExternalStacks, filters = {}) {
      var deferred = $q.defer();

      SwarmService.swarm(endpointId)
        .then(function success(data) {
          var swarm = data;
          filters = { SwarmID: swarm.ID, ...filters };

          return $q.all({
            stacks: Stack.query({ filters: filters }).$promise,
            externalStacks: includeExternalStacks ? service.externalSwarmStacks() : [],
          });
        })
        .then(function success(data) {
          var stacks = data.stacks.map(function (item) {
            return new StackViewModel(item, item.EndpointId != endpointId);
          });
          var externalStacks = data.externalStacks;
          const result = service.unionStacks(stacks, externalStacks);
          deferred.resolve(result);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to retrieve stacks', err: err });
        });

      return deferred.promise;
    };

    service.remove = function (stack, external, endpointId, removeVolumes) {
      var deferred = $q.defer();

      Stack.remove({ id: stack.Id ? stack.Id : stack.Name, external: external, endpointId: endpointId, removeVolumes: removeVolumes })
        .$promise.then(function success() {
          deferred.resolve();
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to remove the stack', err: err });
        });

      return deferred.promise;
    };

    service.removeKubernetesStacksByName = function (name, namespace, external, endpointId) {
      var deferred = $q.defer();
      StackByName.remove({ name: name, external: external, endpointId: endpointId, namespace: namespace })
        .$promise.then(function success() {
          deferred.resolve();
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to remove the stack', err: err });
        });

      return deferred.promise;
    };

    service.associate = function (stack, endpointId, orphanedRunning) {
      var deferred = $q.defer();

      if (stack.Type == 1) {
        SwarmService.swarm(endpointId)
          .then(function success(data) {
            const swarm = data;
            return Stack.associate({ id: stack.Id, endpointId: endpointId, swarmId: swarm.ID, orphanedRunning }).$promise;
          })
          .then(function success(data) {
            deferred.resolve(data);
          })
          .catch(function error(err) {
            deferred.reject({ msg: 'Unable to associate the stack', err: err });
          });
      } else {
        Stack.associate({ id: stack.Id, endpointId: endpointId, orphanedRunning })
          .$promise.then(function success(data) {
            deferred.resolve(data);
          })
          .catch(function error(err) {
            deferred.reject({ msg: 'Unable to associate the stack', err: err });
          });
      }

      return deferred.promise;
    };

    service.updateStack = function (stack, stackFile, env, prune, webhook, repullImageAndRedeploy, rollbackTo, registries) {
      var payload = {
        id: stack.Id,
        StackFileContent: stackFile,
        Env: env,
        Prune: prune,
        Webhook: webhook,
        RepullImageAndRedeploy: repullImageAndRedeploy,
        RollbackTo: rollbackTo,
      };

      if (registries && registries.length > 0) {
        payload.Registries = registries.map(function (registry) {
          return registry.Id;
        });
      }

      return Stack.update({ endpointId: stack.EndpointId }, payload).$promise;
    };

    service.updateKubeStack = function (stack, { stackFile, gitConfig, webhookId, stackName }) {
      let payload = {};

      if (stackFile) {
        payload = {
          StackFileContent: stackFile,
          StackName: stackName,
        };
      } else {
        payload = {
          AutoUpdate: transformAutoUpdateViewModel(gitConfig.AutoUpdate, webhookId),
          RepositoryReferenceName: gitConfig.RefName,
          RepositoryAuthentication: gitConfig.RepositoryAuthentication,
          RepositoryUsername: gitConfig.RepositoryUsername,
          RepositoryPassword: gitConfig.RepositoryPassword,
          RepositoryAuthorizationType: gitConfig.RepositoryAuthorizationType,
          TLSSkipVerify: gitConfig.TLSSkipVerify,
          StackName: stackName,
        };
      }

      return Stack.update({ id: stack.Id, endpointId: stack.EndpointId }, payload).$promise;
    };

    service.createComposeStackFromFileUpload = function (name, stackFile, env, endpointId, webhook, registries) {
      return FileUploadService.createComposeStack(name, stackFile, env, endpointId, webhook, registries);
    };

    service.createSwarmStackFromFileUpload = function (name, stackFile, env, endpointId, webhook) {
      var deferred = $q.defer();

      SwarmService.swarm(endpointId)
        .then(function success(data) {
          var swarm = data;
          return FileUploadService.createSwarmStack(name, swarm.ID, stackFile, env, endpointId, webhook);
        })
        .then(function success(data) {
          deferred.resolve(data.data);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to create the stack', err: err });
        });

      return deferred.promise;
    };
    service.createComposeStackFromFileContent = function (name, stackFileContent, env, endpointId, webhook, registries) {
      var payload = {
        Name: name,
        StackFileContent: stackFileContent,
        Env: env,
        Webhook: webhook,
      };

      if (registries && registries.length > 0) {
        payload.Registries = registries.map(function (registry) {
          return registry.Id;
        });
      }

      return Stack.create({ endpointId: endpointId }, { method: 'string', type: 'standalone', ...payload }).$promise;
    };

    service.createSwarmStackFromFileContent = function (name, stackFileContent, env, endpointId, webhook, registries) {
      var deferred = $q.defer();

      SwarmService.swarm(endpointId)
        .then(function success(swarm) {
          var payload = {
            Name: name,
            SwarmID: swarm.ID,
            StackFileContent: stackFileContent,
            Env: env,
            Webhook: webhook,
          };

          if (registries && registries.length > 0) {
            payload.Registries = registries.map(function (registry) {
              return registry.Id;
            });
          }

          return Stack.create({ endpointId: endpointId }, { method: 'string', type: 'swarm', ...payload }).$promise;
        })
        .then(function success(data) {
          deferred.resolve(data);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to create the stack', err: err });
        });

      return deferred.promise;
    };

    service.createComposeStackFromGitRepository = function (name, repositoryOptions, env, endpointId, registries) {
      var payload = {
        Name: name,
        RepositoryURL: repositoryOptions.RepositoryURL,
        RepositoryReferenceName: repositoryOptions.RepositoryReferenceName,
        ComposeFile: repositoryOptions.ComposeFilePathInRepository,
        AdditionalFiles: repositoryOptions.AdditionalFiles,
        RepositoryAuthentication: repositoryOptions.RepositoryAuthentication,
        RepositoryUsername: repositoryOptions.RepositoryUsername,
        RepositoryPassword: repositoryOptions.RepositoryPassword,
        RepositoryAuthorizationType: repositoryOptions.RepositoryAuthorizationType,
        FromAppTemplate: repositoryOptions.FromAppTemplate,
        Env: env,
        SupportRelativePath: repositoryOptions.SupportRelativePath,
        FilesystemPath: repositoryOptions.FilesystemPath,
        TLSSkipVerify: repositoryOptions.TLSSkipVerify,
      };

      if (repositoryOptions.AutoUpdate) {
        payload.AutoUpdate = repositoryOptions.AutoUpdate;
      }

      if (registries && registries.length > 0) {
        payload.Registries = registries.map(function (registry) {
          return registry.Id;
        });
      }

      return Stack.create({ endpointId: endpointId }, { method: 'repository', type: 'standalone', ...payload }).$promise;
    };

    service.createSwarmStackFromGitRepository = function (name, repositoryOptions, env, endpointId, registries) {
      var deferred = $q.defer();

      SwarmService.swarm(endpointId)
        .then(function success(data) {
          var swarm = data;
          var payload = {
            Name: name,
            SwarmID: swarm.ID,
            RepositoryURL: repositoryOptions.RepositoryURL,
            RepositoryReferenceName: repositoryOptions.RepositoryReferenceName,
            ComposeFile: repositoryOptions.ComposeFilePathInRepository,
            AdditionalFiles: repositoryOptions.AdditionalFiles,
            RepositoryAuthentication: repositoryOptions.RepositoryAuthentication,
            RepositoryUsername: repositoryOptions.RepositoryUsername,
            RepositoryPassword: repositoryOptions.RepositoryPassword,
            RepositoryAuthorizationType: repositoryOptions.RepositoryAuthorizationType,
            FromAppTemplate: repositoryOptions.FromAppTemplate,
            Env: env,
            SupportRelativePath: repositoryOptions.SupportRelativePath,
            FilesystemPath: repositoryOptions.FilesystemPath,
            TLSSkipVerify: repositoryOptions.TLSSkipVerify,
          };

          if (repositoryOptions.AutoUpdate) {
            payload.AutoUpdate = repositoryOptions.AutoUpdate;
          }

          if (registries && registries.length > 0) {
            payload.Registries = registries.map(function (registry) {
              return registry.Id;
            });
          }

          return Stack.create({ endpointId: endpointId }, { method: 'repository', type: 'swarm', ...payload }).$promise;
        })
        .then(function success(data) {
          deferred.resolve(data);
        })
        .catch(function error(err) {
          deferred.reject({ msg: 'Unable to create the stack', err: err });
        });

      return deferred.promise;
    };

    async function kubernetesDeployAsync(endpointId, method, payload) {
      try {
        await Stack.create({ endpointId: endpointId }, { method, type: 'kubernetes', ...payload }).$promise;
      } catch (err) {
        throw { err: err };
      }
    }

    service.kubernetesDeploy = function (endpointId, method, payload) {
      return $async(kubernetesDeployAsync, endpointId, method, payload);
    };

    service.start = start;
    function start(endpointId, id) {
      return Stack.start({ id, endpointId }).$promise;
    }

    service.stop = stop;
    function stop(endpointId, id) {
      return Stack.stop({ endpointId, id }).$promise;
    }

    function updateGit(id, endpointId, env, prune, gitConfig, pullImage, registries) {
      var payload = {
        env,
        prune,
        RepositoryReferenceName: gitConfig.RefName,
        RepositoryAuthentication: gitConfig.RepositoryAuthentication,
        RepositoryUsername: gitConfig.RepositoryUsername,
        RepositoryPassword: gitConfig.RepositoryPassword,
        RepositoryAuthorizationType: gitConfig.RepositoryAuthorizationType,
        PullImage: pullImage,
      };

      if (registries && registries.length > 0) {
        payload.Registries = registries.map(function (registry) {
          return registry.Id;
        });
      }

      return Stack.updateGit({ endpointId, id }, payload).$promise;
    }

    function updateKubeGit(id, endpointId, namespace, gitConfig) {
      return Stack.updateGit(
        { endpointId, id },
        {
          Namespace: namespace,
          RepositoryReferenceName: gitConfig.RefName,
          RepositoryAuthentication: gitConfig.RepositoryAuthentication,
          RepositoryUsername: gitConfig.RepositoryUsername,
          RepositoryPassword: gitConfig.RepositoryPassword,
          RepositoryAuthorizationType: gitConfig.RepositoryAuthorizationType,
          StackName: gitConfig.StackName,
        }
      ).$promise;
    }

    service.updateGitStackSettings = function (id, endpointId, env, gitConfig, webhookId, registries) {
      var payload = {
        AutoUpdate: transformAutoUpdateViewModel(gitConfig.AutoUpdate, webhookId),
        Env: env,
        RepositoryReferenceName: gitConfig.RefName,
        RepositoryAuthentication: gitConfig.RepositoryAuthentication,
        RepositoryUsername: gitConfig.RepositoryUsername,
        RepositoryPassword: gitConfig.RepositoryPassword,
        RepositoryAuthorizationType: gitConfig.RepositoryAuthorizationType,
        Prune: gitConfig.Option.Prune,
        TLSSkipVerify: gitConfig.TLSSkipVerify,
      };

      if (registries && registries.length > 0) {
        payload.Registries = registries.map(function (registry) {
          return registry.Id;
        });
      }

      return Stack.updateGitStackSettings({ endpointId, id }, payload).$promise;
    };

    return service;
  },
]);
